import React, { createContext, useState, useRef, RefObject, useContext, useEffect } from "react";

import { isAvailableOrgPageStepKey } from "./hooks/useGetAllOrgPageStepsInOrder";
import { OrgPageLiveEditorStep, OrgPageLiveEditorStepKey } from "./Steps/OrgPageLiveEditorStep.enum";

import { OrganizationPageOutput } from "@/common/types/trpc";
import { useStepNavigation } from "@/components/LiveEditorElements/Navigation/useStepNavigation";

// Live data type for real-time preview updates
export type LiveOrgPageData = OrganizationPageOutput;

type SaveModalState = {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  closeModalAndExecuteActions: () => void;
};

// Type for org-page apiObject with required common props
export type OrgPageApiObject = OrganizationPageOutput;
interface LiveOrgPageEditorContextType {
  currentStepIndex: OrgPageLiveEditorStep;
  currentStepKey: OrgPageLiveEditorStepKey;
  updateCurrentStep: (stepKey: OrgPageLiveEditorStepKey) => void;
  totalNumberOfSteps: number;
  liveEditorStepsKeys: OrgPageLiveEditorStepKey[];
  getIndexStepFromKey: (key: OrgPageLiveEditorStepKey) => OrgPageLiveEditorStep;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  apiOrgPageObject: OrgPageApiObject;
  updateApiOrgPageObject: (orgPageObject: OrgPageApiObject) => void;
  liveOrgPageData?: LiveOrgPageData;
  setLiveOrgPageData: (data: LiveOrgPageData) => void;
  previewDevice: "mobile" | "desktop";
  setPreviewDevice: (mode: "mobile" | "desktop") => void;
  previewScale: number;
  setPreviewScale: (scale: number) => void;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  saveCurrentStep: RefObject<() => Promise<boolean>>;
  isStepChanging: boolean;
  setIsStepChanging: (isStepChanging: boolean) => void;
  isFormDirty: boolean;
  setIsFormDirty: (isDirty: boolean) => void;
  resetForm: RefObject<() => void>;
  isNavigationOpen: boolean;
  setIsNavigationOpen: (isNavigationOpen: boolean) => void;
  saveModalState: SaveModalState;
  performActionWithSaveGuard: (params: { action: () => void | Promise<void> }) => void;
  isMobilePreviewOpen: boolean;
  setIsMobilePreviewOpen: (isOpen: boolean) => void;
  footerPortalNode: HTMLDivElement | null;
  setFooterPortalNode: (node: HTMLDivElement | null) => void;
  isContactDialogOpenInPreview: boolean;
  setIsContactDialogOpenInPreview: (isOpen: boolean) => void;
  isShareModalOpen: boolean;
  setIsShareModalOpen: (isOpen: boolean) => void;
}

const defaultValues: LiveOrgPageEditorContextType = {
  currentStepIndex: 0,
  currentStepKey: "color",
  updateCurrentStep: () => {},
  totalNumberOfSteps: 0,
  liveEditorStepsKeys: [],
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  getIndexStepFromKey: () => 0,
  apiOrgPageObject: {} as OrgPageApiObject,
  updateApiOrgPageObject: () => {},
  liveOrgPageData: undefined,
  setLiveOrgPageData: () => {},
  previewDevice: "desktop",
  setPreviewDevice: () => {},
  previewScale: 1,
  setPreviewScale: () => {},
  iframeRef: { current: null },
  saveCurrentStep: { current: () => Promise.resolve(true) },
  isStepChanging: false,
  setIsStepChanging: () => {},
  saveModalState: {
    isOpen: false,
    openModal: () => {},
    closeModal: () => {},
    closeModalAndExecuteActions: () => {},
  },
  isFormDirty: false,
  setIsFormDirty: () => {},
  resetForm: { current: () => {} },
  isNavigationOpen: false,
  setIsNavigationOpen: () => {},
  performActionWithSaveGuard: () => {},
  isMobilePreviewOpen: false,
  setIsMobilePreviewOpen: () => {},
  footerPortalNode: null,
  setFooterPortalNode: () => {},
  isContactDialogOpenInPreview: false,
  setIsContactDialogOpenInPreview: () => {},
  isShareModalOpen: false,
  setIsShareModalOpen: () => {},
};

const LiveOrgPageEditorContext = createContext<LiveOrgPageEditorContextType>(defaultValues);

interface LiveOrgPageEditorProviderProps {
  steps: { key: OrgPageLiveEditorStepKey; shouldBeSkip?: boolean }[];
  apiOrgPageObject: OrgPageApiObject;
  setApiOrgPageObject: (orgPageObject: OrgPageApiObject) => void;
  children: (params: LiveOrgPageEditorContextType) => React.ReactNode;
}

export const LiveOrgPageEditorProvider = ({
  children,
  steps,
  apiOrgPageObject,
  setApiOrgPageObject,
}: LiveOrgPageEditorProviderProps) => {
  const {
    currentStepIndex,
    currentStepKey,
    updateCurrentStep,
    goToNextStep,
    goToPreviousStep,
    getIndexStepFromKey,
    liveEditorStepsKeys,
  } = useStepNavigation<OrgPageLiveEditorStepKey>({
    steps,
    isValidStepKey: isAvailableOrgPageStepKey,
    fallbackStepKey: "color",
  });

  const [previewDevice, setPreviewDevice] = useState<"mobile" | "desktop">("desktop");
  const [previewScale, setPreviewScale] = useState<number>(1);
  const [liveOrgPageData, setLiveOrgPageData] = useState<LiveOrgPageData | undefined>(undefined);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const saveCurrentStep = useRef<() => Promise<boolean>>(() => Promise.resolve(true));
  const [isFormDirty, setIsFormDirty] = useState(false);
  const resetForm = useRef<() => void>(() => {});

  const [isNavigationOpen, setIsNavigationOpen] = useState(true);

  const totalNumberOfSteps = liveEditorStepsKeys.length;

  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const pendingActionRef = useRef<() => void>(() => {});
  const saveModalState: SaveModalState = {
    isOpen: isSaveModalOpen,
    openModal: () => setIsSaveModalOpen(true),
    closeModal: () => setIsSaveModalOpen(false),
    closeModalAndExecuteActions: () => {
      setIsSaveModalOpen(false);
      pendingActionRef.current();
      pendingActionRef.current = () => {};
    },
  };
  const performActionWithSaveGuard = ({ action }: { action: () => void | Promise<void> }) => {
    // If the form is dirty, we open the save modal to ask confirmation to save the changes before executing the callback.
    if (isFormDirty) {
      pendingActionRef.current = action;
      setIsSaveModalOpen(true);
    } else {
      // If the form is not dirty, we directly execute the callback without opening the save modal.
      action();
    }
  };

  const [isStepChanging, setIsStepChanging] = useState(false);

  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  const [footerPortalNode, setFooterPortalNode] = useState<HTMLDivElement | null>(null);
  const [isContactDialogOpenInPreview, setIsContactDialogOpenInPreview] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Reset form dirty state when navigating to a new step
  // This prevents stale dirty state from blocking navigation
  useEffect(() => {
    setIsFormDirty(false);
  }, [currentStepKey]);

  const contextValue: LiveOrgPageEditorContextType = {
    currentStepIndex,
    currentStepKey,
    updateCurrentStep,
    goToNextStep,
    goToPreviousStep,
    liveEditorStepsKeys,
    apiOrgPageObject,
    updateApiOrgPageObject: setApiOrgPageObject,
    liveOrgPageData,
    setLiveOrgPageData,
    previewDevice,
    setPreviewDevice,
    previewScale,
    setPreviewScale,
    iframeRef,
    saveCurrentStep,
    isStepChanging,
    setIsStepChanging,
    saveModalState,
    isFormDirty,
    setIsFormDirty,
    resetForm,
    isNavigationOpen,
    setIsNavigationOpen,
    totalNumberOfSteps,
    performActionWithSaveGuard,
    getIndexStepFromKey,
    isMobilePreviewOpen,
    setIsMobilePreviewOpen,
    footerPortalNode,
    setFooterPortalNode,
    isContactDialogOpenInPreview,
    setIsContactDialogOpenInPreview,
    isShareModalOpen,
    setIsShareModalOpen,
  };

  return (
    <LiveOrgPageEditorContext.Provider value={contextValue}>{children(contextValue)}</LiveOrgPageEditorContext.Provider>
  );
};

export const useLiveOrgPageEditorContext = () => {
  return useContext(LiveOrgPageEditorContext);
};
